import type { DetailedCalculationResult } from './types';

/**
 * Input values for displacement cost calculation
 */
interface DisplacementCostInputs {
	roomsDisplaced: number;
	transientRate: number;
	rate_requested: number;
}

/**
 * Calculation steps for displacement cost calculation
 */
interface DisplacementCostSteps {
	formula: { value: string; description: string };
	result: { value: number; description: string };
}

/**
 * Calculate the displacement cost when transient rooms are replaced by group rooms
 */
export const calculateDisplacementCost = (
	roomsDisplaced: number,
	transientRate: number,
	rate_requested: number,
): DetailedCalculationResult<
	number,
	DisplacementCostInputs,
	DisplacementCostSteps
> => {
	const result = roomsDisplaced * (transientRate - rate_requested) * -1;

	return {
		value: result,
		details: {
			inputValues: {
				roomsDisplaced,
				transientRate,
				rate_requested,
			},
			calculations: {
				formula: {
					value: 'roomsDisplaced * (transientRate - rate_requested) * -1',
					description:
						'Calculates the cost of displacing transient bookings with group bookings',
				},
				result: {
					value: result,
					description: 'The final displacement cost calculation result',
				},
			},
		},
	};
};

/**
 * Input values for incremental commission calculation
 */
interface IncrementalCommissionInputs {
	roomsDisplaced: number;
	fcstADR: number;
	commission: number;
	rate_requested: number;
	capacity: number;
	totalForecast: number;
	roomsRequested: number;
}

/**
 * Calculation steps for incremental commission calculation
 */
interface IncrementalCommissionSteps {
	groupCommissionRate: { value: number; description: string };
	standardCommissionRate: { value: number; description: string };
	groupCommissionAmount: { value: number; description: string };
	standardCommissionAmount: { value: number; description: string };
	commissionDifferencePerRoom: { value: number; description: string };
	incrementalRooms: { value: number; description: string };
	displacedRooms: { value: number; description: string };
	incrementalRoomCommission: { value: number; description: string };
	displacedRoomCommission: { value: number; description: string };
	totalIncrementalCommission: { value: number; description: string };
}

/**
 * Calculate incremental commission costs
 */
export const calculateIncrementalCommission = (
	roomsDisplaced: number,
	fcstADR: number,
	commission: number,
	rate_requested: number,
	capacity: number,
	totalForecast: number,
	roomsRequested: number,
): DetailedCalculationResult<
	number,
	IncrementalCommissionInputs,
	IncrementalCommissionSteps
> => {
	// Convert group commission from percentage to decimal
	const groupCommissionRate = commission / 100;
	// Standard commission rate for transient business (7%)
	const standardCommissionRate = 0.07;

	// Determine how many rooms are truly incremental
	// Incremental rooms are the difference between capacity and total forecast
	// but they are capped at the number of rooms requested
	const incrementalRooms = Math.min(
		roomsRequested,
		Math.max(0, capacity - totalForecast),
	);
	const displacedRooms = Math.max(0, roomsRequested - incrementalRooms);

	// Commission on incremental rooms (these would not have been sold without the group)
	const incrementalRoomCommission =
		incrementalRooms * groupCommissionRate * rate_requested * -1;

	// Commission differential for displaced rooms
	const standardCommissionAmount = standardCommissionRate * fcstADR;
	const groupCommissionAmount = groupCommissionRate * rate_requested;
	const commissionDifferencePerRoom =
		standardCommissionAmount - groupCommissionAmount;

	const displacedRoomCommission =
		displacedRooms * commissionDifferencePerRoom * -1;

	const totalIncrementalCommission =
		incrementalRoomCommission + displacedRoomCommission;

	// Return detailed breakdown of calculation steps
	return {
		value: totalIncrementalCommission,
		details: {
			inputValues: {
				roomsDisplaced,
				fcstADR,
				commission,
				rate_requested,
				capacity,
				totalForecast,
				roomsRequested,
			},
			calculations: {
				groupCommissionRate: {
					value: groupCommissionRate,
					description:
						'Group commission percentage converted to decimal (commission / 100)',
				},
				standardCommissionRate: {
					value: standardCommissionRate,
					description:
						'Standard commission rate for transient business (fixed at 7%)',
				},
				groupCommissionAmount: {
					value: groupCommissionAmount,
					description:
						'Commission amount per room for group booking (groupCommissionRate * rate_requested)',
				},
				standardCommissionAmount: {
					value: standardCommissionAmount,
					description:
						'Standard commission amount per room for transient business (standardCommissionRate * fcstADR)',
				},
				commissionDifferencePerRoom: {
					value: commissionDifferencePerRoom,
					description:
						'Difference in commission per room between transient and group (standardCommissionAmount - groupCommissionAmount)',
				},
				incrementalRooms: {
					value: incrementalRooms,
					description:
						'Rooms sold that are truly incremental (capacity - totalForecast)',
				},
				displacedRooms: {
					value: displacedRooms,
					description:
						'Group rooms that displaced forecasted transient rooms (roomsRequested - incrementalRooms)',
				},
				incrementalRoomCommission: {
					value: incrementalRoomCommission,
					description:
						'Commission paid on incremental rooms (incrementalRooms * groupCommissionAmount * -1)',
				},
				displacedRoomCommission: {
					value: displacedRoomCommission,
					description:
						'Incremental cost due to commission rate difference on displaced rooms (displacedRooms * commissionDifferencePerRoom * -1)',
				},
				totalIncrementalCommission: {
					value: totalIncrementalCommission,
					description:
						'Sum of commission on incremental rooms and displaced room commission differential',
				},
			},
		},
	};
};

/**
 * Input values for planners advantage cost calculation
 */
interface PlannersAdvCostInputs {
	points: number;
	rate_requested: number;
	rooms_requested: number;
}

/**
 * Calculation steps for planners advantage cost
 */
interface PlannersAdvCostSteps {
	pointValueRate: { value: number; description: string };
	formula: { value: string; description: string };
	result: { value: number; description: string };
}

/**
 * Calculate the cost of planners advantage points
 */
export const calculatePlannersAdvCost = (
	points: number,
	rate_requested: number,
	rooms_requested: number,
): DetailedCalculationResult<
	number,
	PlannersAdvCostInputs,
	PlannersAdvCostSteps
> => {
	const pointValueRate = 0.0055;
	const result =
		points * rate_requested * rooms_requested * pointValueRate * -1;

	return {
		value: result,
		details: {
			inputValues: {
				points,
				rate_requested,
				rooms_requested,
			},
			calculations: {
				pointValueRate: {
					value: pointValueRate,
					description:
						'Dollar value per point in the Planners Advantage Program',
				},
				formula: {
					value:
						'points * rate_requested * rooms_requested * pointValueRate * -1',
					description:
						'Calculates the total cost of awarding Planners Advantage points',
				},
				result: {
					value: result,
					description: 'The final cost of Planners Advantage points',
				},
			},
		},
	};
};

/**
 * Input values for variable operating expenses calculation
 */
interface VariableOpExInputs {
	netRooms: number;
	directVarOpex: number;
}

/**
 * Calculation steps for variable operating expenses
 */
interface VariableOpExSteps {
	formula: { value: string; description: string };
	result: { value: number; description: string };
}

/**
 * Calculate variable operating expenses
 */
export const calculateVariableOpEx = (
	netRooms: number,
	directVarOpex: number,
): DetailedCalculationResult<number, VariableOpExInputs, VariableOpExSteps> => {
	const result = netRooms * directVarOpex * -1;

	return {
		value: result,
		details: {
			inputValues: {
				netRooms,
				directVarOpex,
			},
			calculations: {
				formula: {
					value: 'netRooms * directVarOpex * -1',
					description:
						'Calculates the incremental variable operating expenses based on net rooms added',
				},
				result: {
					value: result,
					description: 'The final incremental variable operating expense',
				},
			},
		},
	};
};

/**
 * Input values for cost of guests walked calculation
 */
interface CostOfGuestsWalkedInputs {
	guestsWalked: number;
	rate: number;
}

/**
 * Calculation steps for cost of guests walked
 */
interface CostOfGuestsWalkedSteps {
	formula: { value: string; description: string };
	result: { value: number; description: string };
}

/**
 * Calculate the cost of guests walked
 */
export const calculateCostOfGuestsWalked = (
	guestsWalked: number,
	rate: number,
): DetailedCalculationResult<
	number,
	CostOfGuestsWalkedInputs,
	CostOfGuestsWalkedSteps
> => {
	const result = guestsWalked * rate * -1;

	return {
		value: result,
		details: {
			inputValues: {
				guestsWalked,
				rate,
			},
			calculations: {
				formula: {
					value: 'guestsWalked * rate * -1',
					description:
						'Calculates the cost of walking guests (relocating them to another hotel)',
				},
				result: {
					value: result,
					description: 'The final cost of guests walked',
				},
			},
		},
	};
};

/**
 * Input values for complimentary room cost calculation
 */
interface CompRoomCostInputs {
	compRooms: number;
	rate: number;
}

/**
 * Calculation steps for complimentary room cost
 */
interface CompRoomCostSteps {
	formula: { value: string; description: string };
	result: { value: number; description: string };
}

/**
 * Calculate the cost of complimentary rooms
 */
export const calculateCompRoomCost = (
	compRooms: number,
	rate: number,
): DetailedCalculationResult<number, CompRoomCostInputs, CompRoomCostSteps> => {
	const result = compRooms * rate * -1;

	return {
		value: result,
		details: {
			inputValues: {
				compRooms,
				rate,
			},
			calculations: {
				formula: {
					value: 'compRooms * rate * -1',
					description:
						'Calculates the cost of providing complimentary rooms to the group',
				},
				result: {
					value: result,
					description: 'The final cost of complimentary rooms',
				},
			},
		},
	};
};

/**
 * Input values for rebate cost calculation
 */
interface RebateCostInputs {
	rebate: number;
	rooms_requested: number;
}

/**
 * Calculation steps for rebate cost
 */
interface RebateCostSteps {
	formula: { value: string; description: string };
	result: { value: number; description: string };
}

/**
 * Calculate the cost of rebates
 */
export const calculateRebateCost = (
	rebate: number,
	rooms_requested: number,
): DetailedCalculationResult<number, RebateCostInputs, RebateCostSteps> => {
	const result = rebate * rooms_requested * -1;

	return {
		value: result,
		details: {
			inputValues: {
				rebate,
				rooms_requested,
			},
			calculations: {
				formula: {
					value: 'rebate * rooms_requested * -1',
					description:
						'Calculates the cost of rebates based on number of rooms requested',
				},
				result: {
					value: result,
					description: 'The final cost of rebates',
				},
			},
		},
	};
};

/**
 * Input values for net benefit calculation
 */
interface NetBenefitInputs {
	incrementalRoomRev: number;
	costOfGuestsWalked: number;
	compRoomCost: number;
	rebateCost: number;
	displacementCost: number;
	incrementalVariableCost: number;
	incrementalCommission: number;
	fbBenefit: number;
	plannersAdvCost: number;
}

/**
 * Calculation steps for net benefit
 */
interface NetBenefitSteps {
	revenueComponents: { value: number; description: string };
	costComponents: { value: number; description: string };
	total: { value: number; description: string };
}

/**
 * Calculate net benefit by summing all revenue and cost components
 */
export const calculateNetBenefit = (
	incrementalRoomRev: number,
	costOfGuestsWalked: number,
	compRoomCost: number,
	rebateCost: number,
	displacementCost: number,
	incrementalVariableCost: number,
	incrementalCommission: number,
	fbBenefit: number,
	plannersAdvCost: number,
): DetailedCalculationResult<number, NetBenefitInputs, NetBenefitSteps> => {
	const revenueComponents = incrementalRoomRev + fbBenefit;
	const costComponents =
		costOfGuestsWalked +
		compRoomCost +
		rebateCost +
		displacementCost +
		incrementalVariableCost +
		incrementalCommission +
		plannersAdvCost;

	const total = revenueComponents + costComponents;

	return {
		value: total,
		details: {
			inputValues: {
				incrementalRoomRev,
				costOfGuestsWalked,
				compRoomCost,
				rebateCost,
				displacementCost,
				incrementalVariableCost,
				incrementalCommission,
				fbBenefit,
				plannersAdvCost,
			},
			calculations: {
				revenueComponents: {
					value: revenueComponents,
					description:
						'Sum of all revenue components (incremental room revenue and F&B benefit)',
				},
				costComponents: {
					value: costComponents,
					description:
						'Sum of all cost components (walked guests, comp rooms, rebates, displacement, variable costs, commission, planner points)',
				},
				total: {
					value: total,
					description: 'Net benefit (revenue components + cost components)',
				},
			},
		},
	};
};
